/**
 * 센서 월드(PICNIC-MB-FIELD-001 v0.2 §2 통로 ④ · §3 유형 S · §6 5단계 — 사용자 결정 '가: 안내형').
 *
 * MakeCode 시뮬레이터에는 센서 값을 바깥에서 넣는 길이 없다(보드는 라디오 · 확장 메시지 · 시리얼만 받고 시리얼은 비어 있다).
 * 그래서 놀이터가 **이끌고 아이가 시뮬레이터의 슬라이더를 움직인다** — "동굴에 들어왔어요 — 빛을 어둡게". 놀이터는 두 가지를 본다:
 *  ① 센서 값(시뮬레이터 칸의 놀이터 연결 확장이 요청받은 센서를 줄 끝에 붙인다 · lib/live Sensors) — 구역의 범위 안인가
 *  ② 보드 반응(화면) — 그 구역에서 아이 프로그램이 해야 할 모습인가(켜짐 · 꺼짐 · 아이콘 · 글자 · 바뀜)
 * 둘 다 맞은 채 잠깐(holdMs) 이어지면 구역 통과. 진짜 보드(센서 값이 오지 않는다 — 아이가 손으로 가리고 데우고 손뼉 친다)는 ②만 본다.
 * 순수 상태(DOM · 시계 없음) — 지난 시간 ms 와 그때의 보드 모습.
 */
import type { MbFrame, Sensors } from "./live";
import { iconOf, isBlank } from "./live";
import { glyphOf, bitsOfLeds } from "./font";

export type SensorName = keyof Sensors;

/** 그 구역에서 보드가 보여야 할 모습 */
export type Expect =
  | { readonly kind: "lit" }
  | { readonly kind: "dark" }
  | { readonly kind: "icon"; readonly icons: readonly string[] }
  | { readonly kind: "notIcon"; readonly icons: readonly string[] }
  | { readonly kind: "glyph"; readonly chars: readonly string[] }
  /** 구역에 들어설 때와 다른 화면(켜고 끄기를 바꾼다 — 박수 램프) */
  | { readonly kind: "changed" }
  /** 보드 반응은 따지지 않는다 — 센서 값만(기록하는 동안 · 하루 해 시계) */
  | { readonly kind: "any" }
  /** 켜진 칸이 n 개 이상 · 이하(막대그래프 — 자석 세기) */
  | { readonly kind: "atLeast"; readonly n: number }
  | { readonly kind: "atMost"; readonly n: number }
  /** 구역 안에서 n 번 흔들기(보드 줄 S — 아침 기상 도우미) */
  | { readonly kind: "shakes"; readonly n: number };

export interface Range {
  readonly sensor: SensorName;
  readonly min: number;
  readonly max: number;
}

export interface Zone {
  /** i18n `student.microbit.sensor.zone.<key>` · 구역 그림 */
  readonly key: string;
  readonly emoji: string;
  /** 구역을 정하는 센서 — 없으면 보드 반응만 본다(시뮬레이터로 만들 수 없는 것: 자유낙하 · 자석 · 버튼으로 다시 보기) */
  readonly sensor?: SensorName;
  readonly min: number;
  readonly max: number;
  /** 두 번째 센서 범위(온도 × 빛 — 쾌적 교실) */
  readonly also?: Range;
  /**
   * 센서 없는 되풀이 구역에서 '구역을 나왔다'로 보는 보드 모습(경고가 꺼짐 · 화살표로 돌아감) — 나왔다가 다시 보여야 한 번 더 센다.
   */
  readonly away?: Expect;
  readonly expect: Expect;
  /** 맞은 채 이만큼 이어지면 통과(ms) */
  readonly holdMs: number;
}

export interface SensorScene {
  readonly id: string;
  readonly lesson: string;
  readonly sensors: readonly SensorName[];
  readonly zones: readonly Zone[];
  /** repeat 면 구역(하나)을 이만큼 되풀이 — 들어갔다 나와야 한 번 더 센다(박수 · 깜빡) */
  readonly repeat?: number;
  /** 화면 위에 늘 거는 문구(i18n `student.microbit.sensor.banner.<id>` — 끌 수 없다 · L35 §7 결정 5) */
  readonly banner?: boolean;
}

const z = (key: string, emoji: string, sensor: SensorName, min: number, max: number, expect: Expect, holdMs = 1000, also?: Range): Zone =>
  ({ key, emoji, sensor, min, max, expect, holdMs, ...(also ? { also } : {}) });
/** 센서 없는 구역 — 보드 반응만(away 면 되풀이에서 나왔다가 다시) */
const r = (key: string, emoji: string, expect: Expect, holdMs = 500, away?: Expect): Zone =>
  ({ key, emoji, min: 0, max: 0, expect, holdMs, ...(away ? { away } : {}) });

/**
 * 첫 묶음 9차시(§4 — 빛 · 온도 · 소리 · 나침반). 범위는 시뮬레이터 슬라이더(빛 · 소리 0~255 · 온도 −5~50 · 나침반 0~359)에서 아이가 쉽게 맞출 만큼 넓게.
 * 보드 반응은 교안 정답 프로그램이 하는 모습(예: 어두우면 하트를 켠다 → lit).
 */
export const SENSOR_SCENES: readonly SensorScene[] = [
  { id: "firefly", lesson: "LE08", sensors: ["light"], zones: [
    z("cave", "🕳️", "light", 0, 60, { kind: "lit" }),
    z("sunny", "🌞", "light", 150, 255, { kind: "dark" }),
  ] },
  { id: "nightWalk", lesson: "L19", sensors: ["light"], zones: [
    z("day", "🌞", "light", 150, 255, { kind: "dark" }),
    z("dusk", "🌆", "light", 0, 80, { kind: "lit" }),
    z("night", "🌙", "light", 0, 30, { kind: "lit" }, 2000),
  ] },
  { id: "fridge", lesson: "L21", sensors: ["light"], zones: [
    z("fridgeOpen", "🚪", "light", 150, 255, { kind: "lit" }),
    z("fridgeClosed", "🧊", "light", 0, 40, { kind: "changed" }),
  ] },
  { id: "seasons", lesson: "LE09", sensors: ["temp"], zones: [
    z("bonfire", "🔥", "temp", 28, 50, { kind: "icon", icons: ["Happy"] }),
    z("snowfield", "⛄", "temp", -5, 8, { kind: "notIcon", icons: ["Happy"] }),
  ] },
  { id: "thermoRooms", lesson: "L20", sensors: ["temp"], zones: [
    z("room16", "🧊", "temp", 13, 18, { kind: "lit" }),
    z("room22", "🙂", "temp", 20, 24, { kind: "changed" }),
    z("room29", "🥵", "temp", 27, 31, { kind: "changed" }),
  ] },
  { id: "magicForest", lesson: "LE07", sensors: ["sound"], repeat: 5, zones: [
    z("clap", "👏", "sound", 128, 255, { kind: "lit" }, 300),
  ] },
  { id: "clapLamp", lesson: "L24", sensors: ["sound"], repeat: 3, zones: [
    z("clapToggle", "💡", "sound", 160, 255, { kind: "changed" }, 300),
  ] },
  { id: "library", lesson: "L25", sensors: ["sound"], zones: [
    z("noisy", "📢", "sound", 180, 255, { kind: "icon", icons: ["No"] }),
    z("quiet", "🤫", "sound", 0, 40, { kind: "notIcon", icons: ["No"] }, 30_000),
  ] },
  { id: "compassField", lesson: "L16", sensors: ["heading"], zones: [
    z("north", "⬆️", "heading", 315, 405, { kind: "glyph", chars: ["N"] }),
    z("east", "🏰", "heading", 45, 135, { kind: "glyph", chars: ["E"] }),
  ] },
  // 둘째 묶음(2026-10-05) — 시뮬레이터로 만들 수 없는 것(자유낙하 · 자석)은 보드 반응만 본다(진짜 보드로)
  { id: "bungee", lesson: "L15", sensors: [], repeat: 3, zones: [
    r("jump", "🪂", { kind: "lit" }, 200, { kind: "dark" }),
  ] },
  { id: "treasureIsland", lesson: "L17", sensors: ["heading"], repeat: 3, zones: [
    r("treasure", "💎", { kind: "icon", icons: ["Yes"] }, 800, { kind: "notIcon", icons: ["Yes"] }),
  ] },
  { id: "magnetRock", lesson: "L18", sensors: [], repeat: 3, zones: [
    r("magnet", "🧲", { kind: "atLeast", n: 15 }, 500, { kind: "atMost", n: 5 }),
  ] },
  { id: "sunDial", lesson: "L22", sensors: ["light"], zones: [
    z("dawn", "🌅", "light", 0, 60, { kind: "any" }, 3000),
    z("noon", "☀️", "light", 150, 255, { kind: "any" }, 3000),
    z("sunset", "🌇", "light", 0, 60, { kind: "any" }, 3000),
    r("replay", "📈", { kind: "changed" }),
  ] },
  { id: "quizShow", lesson: "L35", sensors: ["sound"], banner: true, zones: [
    z("calm", "😌", "sound", 0, 40, { kind: "lit" }),
    z("nervous", "😬", "sound", 80, 150, { kind: "changed" }),
    z("tense", "😱", "sound", 180, 255, { kind: "changed" }),
  ] },
  { id: "classroom", lesson: "X04", sensors: ["temp", "light"], zones: [
    z("comfy", "🛋️", "temp", 18, 26, { kind: "lit" }, 1000, { sensor: "light", min: 120, max: 255 }),
    z("hotBright", "🥵", "temp", 28, 50, { kind: "changed" }, 1000, { sensor: "light", min: 150, max: 255 }),
    z("coldDark", "🥶", "temp", -5, 12, { kind: "changed" }, 1000, { sensor: "light", min: 0, max: 60 }),
    z("hotDark", "🌙", "temp", 28, 50, { kind: "changed" }, 1000, { sensor: "light", min: 0, max: 60 }),
  ] },
  { id: "morningRoom", lesson: "X05", sensors: ["light"], zones: [
    z("sleep", "😴", "light", 0, 40, { kind: "any" }, 2000),
    z("sunrise", "⏰", "light", 150, 255, { kind: "lit" }),
    r("snooze", "💤", { kind: "changed" }, 300),
    r("wake", "🤸", { kind: "shakes", n: 10 }, 0),
  ] },
  { id: "dayLogger", lesson: "X10", sensors: ["light"], zones: [
    z("play", "⚽", "light", 150, 255, { kind: "any" }, 3000),
    z("rest", "🛌", "light", 0, 40, { kind: "any" }, 3000),
    r("replay", "📈", { kind: "changed" }),
  ] },
];

export interface SensorState {
  readonly zone: number;
  readonly held: number;
  readonly count: number;
  readonly done: boolean;
  /** 구역에 들어선 순간의 화면(changed 판정) · 들어가 있는가(repeat — 나와야 한 번 더) */
  readonly entryBits: string | null;
  readonly inside: boolean;
  readonly passedThisVisit: boolean;
  /** 이 구역에서 흔든 수(shakes) · 직전 줄의 흔들기 */
  readonly hits: number;
  readonly prevShake: boolean;
  /**
   * 직전 한 걸음의 화면 — '바뀜' 판정의 기준은 구역에 들어서기 **전** 화면이다. 들어선 순간의 화면을 기준으로 하면, 센서 값이 범위에 든 그 줄에서
   * 아이 프로그램이 이미 그림을 바꿔 둔 경우 끝내 '바뀜'이 안 된다(2026-10-05 운영 실측 — 교실 리모델링 덥고 어두운 방)
   */
  readonly prevBits: string | null;
}

export const initialSensor = (): SensorState => ({ zone: 0, held: 0, count: 0, done: false, entryBits: null, inside: false, passedThisVisit: false, hits: 0, prevShake: false, prevBits: null });

/** 센서 값이 구역 범위 안인가 — 나침반은 360 을 넘는 범위(북쪽 315~405)를 돌려 본다 */
export function inRange(z: Pick<Zone, "sensor" | "min" | "max">, v: number): boolean {
  if (z.sensor === "heading" && z.max > 360) return v >= z.min || v <= z.max - 360;
  return v >= z.min && v <= z.max;
}

const litCount = (leds: readonly number[]) => leds.filter((v) => v > 0).length;

export function expectOk(e: Expect, leds: readonly number[], entryBits: string | null): boolean {
  switch (e.kind) {
    case "lit": return !isBlank(leds);
    case "dark": return isBlank(leds);
    case "icon": return e.icons.includes(iconOf(leds) ?? "");
    case "notIcon": return !isBlank(leds) && !e.icons.includes(iconOf(leds) ?? "");
    case "glyph": return e.chars.includes(glyphOf(leds) ?? "");
    case "changed": return entryBits !== null && bitsOfLeds(leds) !== entryBits;
    case "any": return true;
    case "atLeast": return litCount(leds) >= e.n;
    case "atMost": return litCount(leds) <= e.n;
    case "shakes": return false;   // 흔든 수는 stepSensor 가 센다
  }
}

/**
 * 한 걸음 — 센서 값이 없으면(진짜 보드 · 확장이 아직 요청을 못 받음) 보드 반응만 본다.
 * 순서형: 구역을 차례로 통과. 되풀이형(repeat): 구역에 들어가 통과하고, 나왔다가 다시 들어가야 한 번 더.
 */
export function stepSensor(scene: SensorScene, s: SensorState, dtMs: number, frame: MbFrame | null): SensorState {
  if (s.done || !frame) return s;
  const zone = scene.zones[Math.min(s.zone, scene.zones.length - 1)]!;
  const shakeEdge = frame.shake && !s.prevShake;
  const bits = bitsOfLeds(frame.leds);
  const base = { ...s, prevShake: frame.shake, prevBits: bits };
  const inside = insideZone(zone, frame);
  if (!inside) return { ...base, held: 0, inside: false, entryBits: null, passedThisVisit: false, hits: 0 };
  // 들어서기 전 화면(직전 걸음)이 기준 — 처음 본 줄이면 지금 화면
  const entryBits = s.inside && s.entryBits !== null ? s.entryBits : s.prevBits ?? bits;
  if (scene.repeat && s.passedThisVisit) return { ...base, inside: true, entryBits };
  const hits = s.hits + (shakeEdge ? 1 : 0);
  const ok = zone.expect.kind === "shakes" ? hits >= zone.expect.n : expectOk(zone.expect, frame.leds, entryBits);
  const held = ok ? s.held + dtMs : 0;
  if (!ok || held < zone.holdMs) return { ...base, held, inside: true, entryBits, hits };
  if (scene.repeat) {
    const count = s.count + 1;
    return { ...base, count, held: 0, inside: true, entryBits, hits: 0, passedThisVisit: true, done: count >= scene.repeat };
  }
  const next = s.zone + 1;
  return { ...initialSensor(), zone: next, count: next, done: next >= scene.zones.length, prevShake: frame.shake, prevBits: bits };
}

/**
 * 지금 구역 안인가 — 센서 값이 없으면(진짜 보드에 센서 블록이 없음 · 확장이 아직 요청을 못 받음) 안으로 본다(보드 반응만).
 * 센서 없는 구역: away 가 있으면 그 모습이 아닐 때만 안(되풀이에서 '나왔다가 다시'), 없으면 늘 안.
 */
function insideZone(zone: Zone, frame: MbFrame): boolean {
  if (!zone.sensor) return zone.away ? !expectOk(zone.away, frame.leds, null) : true;
  const v = frame.sensors?.[zone.sensor];
  if (v !== undefined && !inRange(zone, v)) return false;
  const a = zone.also;
  const w = a ? frame.sensors?.[a.sensor] : undefined;
  return !(a && w !== undefined && !inRange(a, w));
}

export const sceneOfLesson = (id: string): SensorScene | null => SENSOR_SCENES.find((x) => x.id === id) ?? null;
